import React, { useState, useEffect } from 'react';
import { Plus, Search, RefreshCw } from 'lucide-react';
import { toast } from 'react-toastify';
import { getTileTypes, createTileType, updateTileType, deleteTileType } from '../api/categories';
import CategoryModal from '../components/CategoryModal';
import CategoryCard from '../components/CategoryCard';
import DeleteConfirmModal from '../components/DeleteConfirmModal';

const TileTypesPage = () => {
  const [tileTypes, setTileTypes] = useState([]);
  const [filteredTileTypes, setFilteredTileTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedTileType, setSelectedTileType] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);

  // Fetch tile types
  const fetchTileTypes = async () => {
    setLoading(true);
    try {
      const data = await getTileTypes();
      setTileTypes(data);
      setFilteredTileTypes(data);
    } catch (error) {
      toast.error('Failed to fetch tile types');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTileTypes();
  }, []);

  // Search filter
  useEffect(() => {
    if (searchTerm.trim() === '') {
      setFilteredTileTypes(tileTypes);
    } else {
      const filtered = tileTypes.filter((tileType) =>
        tileType.name.toLowerCase().includes(searchTerm.toLowerCase())
      );
      setFilteredTileTypes(filtered);
    }
  }, [searchTerm, tileTypes]);

  // Handle create/update with optimistic UI
  const handleSubmit = async (data) => {
    try {
      if (selectedTileType) {
        const updated = await updateTileType(selectedTileType.id, data);
        setTileTypes((prev) =>
          prev.map((t) => (t.id === selectedTileType.id ? { ...t, ...updated } : t))
        );
        toast.success('Tile type updated successfully');
      } else {
        const created = await createTileType(data);
        setTileTypes((prev) => [...prev, created]);
        toast.success('Tile type created successfully');
      }
      setShowModal(false);
      setSelectedTileType(null);
    } catch (error) {
      throw error;
    }
  };

  // Handle edit
  const handleEdit = (tileType) => {
    setSelectedTileType(tileType);
    setShowModal(true);
  };

  // Handle delete
  const handleDelete = (tileType) => {
    setSelectedTileType(tileType);
    setShowDeleteModal(true);
  };

  const confirmDelete = async () => {
    setDeleteLoading(true);
    try {
      await deleteTileType(selectedTileType.id);
      setTileTypes((prev) => prev.filter((t) => t.id !== selectedTileType.id));
      toast.success('Tile type deleted successfully');
      setShowDeleteModal(false);
      setSelectedTileType(null);
    } catch (error) {
      const errorMessage = error.response?.data?.error || 'Failed to delete tile type';
      toast.error(errorMessage);
    } finally {
      setDeleteLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-secondary-offWhite p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h1 className="text-3xl font-bold text-primary-black">Tile Type Management</h1>
              <p className="text-gray-600 mt-1">Manage tile types and categories</p>
            </div>
            <button
              onClick={() => {
                setSelectedTileType(null);
                setShowModal(true);
              }}
              className="flex items-center gap-2 px-4 py-2 bg-accent-gold text-primary-black font-semibold rounded-lg hover:bg-accent-goldDark transition-colors"
            >
              <Plus className="w-5 h-5" />
              Add Tile Type
            </button>
          </div>

          {/* Search and Actions */}
          <div className="flex gap-4">
            <div className="flex-1 relative">
              <Search className="w-5 h-5 text-gray-400 absolute left-3 top-1/2 transform -translate-y-1/2" />
              <input
                type="text"
                placeholder="Search tile types..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-accent-gold focus:border-transparent"
              />
            </div>
            <button
              onClick={fetchTileTypes}
              className="flex items-center gap-2 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
            >
              <RefreshCw className="w-5 h-5" />
              Refresh
            </button>
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          <div className="bg-white rounded-lg shadow-md p-6 border-2 border-accent-gold">
            <h3 className="text-sm font-medium text-gray-600 mb-2">Total Tile Types</h3>
            <p className="text-3xl font-bold text-primary-black">{tileTypes.length}</p>
          </div>
          <div className="bg-white rounded-lg shadow-md p-6 border-2 border-gray-400">
            <h3 className="text-sm font-medium text-gray-600 mb-2">Showing</h3>
            <p className="text-3xl font-bold text-primary-black">{filteredTileTypes.length}</p>
          </div>
          <div className="bg-white rounded-lg shadow-md p-6 border-2 border-gray-400">
            <h3 className="text-sm font-medium text-gray-600 mb-2">Search Results</h3>
            <p className="text-3xl font-bold text-primary-black">
              {searchTerm ? filteredTileTypes.length : '-'}
            </p>
          </div>
        </div>

        {/* Content */}
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-accent-gold"></div>
          </div>
        ) : filteredTileTypes.length === 0 ? (
          <div className="bg-white rounded-lg shadow-md p-12 text-center">
            <p className="text-gray-500 text-lg">
              {searchTerm
                ? 'No tile types found matching your search'
                : 'No tile types yet. Add your first tile type!'}
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredTileTypes.map((tileType) => (
              <CategoryCard
                key={tileType.id}
                category={tileType}
                onEdit={handleEdit}
                onDelete={handleDelete}
              />
            ))}
          </div>
        )}
      </div>

      {/* Modals */}
      <CategoryModal
        isOpen={showModal}
        onClose={() => {
          setShowModal(false);
          setSelectedTileType(null);
        }}
        onSubmit={handleSubmit}
        initialData={selectedTileType}
        title={selectedTileType ? 'Edit Tile Type' : 'Add New Tile Type'}
        categoryType="tile_type"
      />

      <DeleteConfirmModal
        isOpen={showDeleteModal}
        onClose={() => {
          setShowDeleteModal(false);
          setSelectedTileType(null);
        }}
        onConfirm={confirmDelete}
        itemName={selectedTileType?.name}
        loading={deleteLoading}
      />
    </div>
  );
};

export default TileTypesPage;
